import React, { useContext } from 'react';
import { ISubcategoryContext, SubcategoryContext } from '../../../subcategoryState/subcategoryContext';
import { UserRoles, ISubcategoryByRestaurantV2 } from 'types';
import { useRoleAccess } from 'hooks/useRoleAccess';
import { Link } from 'react-router-dom';
import Text from 'components/Text/Text';
import { ImgUploaderByPreSignedUrl } from 'components/ImgUploader/ImgUploaderByPreSignedUrl';
import { Badge } from 'components/Badge';
import DataController from 'components/DataController/DataController';
import classNames from 'classnames';
import style from './Item.module.scss';

interface IProps {
	data: ISubcategoryByRestaurantV2['items'][0];
	index: number;
}

export const Item: React.FC<IProps> = ({ data, index }) => {
	const { setIDForDeletion } = useContext<ISubcategoryContext>(SubcategoryContext);
	const hasEditAccess = useRoleAccess([
		UserRoles.SUPER_USER,
		UserRoles.CLIENT_MANAGER,
		UserRoles.GROUP_MANAGER,
		UserRoles.RESTAURANT_MANAGER,
		UserRoles.TECHNICAL,
	]);
	const subcategoryRowClasses = classNames(style.subcategory, style.row);
	const subcategoryNameClasses = classNames(style.subcategoryName);
	const categoryNameRowClasses = classNames(style.categoryName, style.row);
	const isHiddenRowClasses = classNames(style.isHidden, style.row);
	const availableForRepetitionRowClasses = classNames(style.availableForRepetition, style.row);
	const actionRowClasses = classNames(style.action, style.row);

	const editButtonElement = (
		<div className={style.editItemWrapper}>
			<Link to={`/menu-v2/subcategory/edit?subcategoryID=${data.id}`}>
				<Text translationNameSpace="listing" translationKey="editMainInfo" size="s" />
			</Link>
			<Link to={`/menu-v2/subcategory/edit-restaurant-properties?subcategoryID=${data.id}`}>
				<Text translationNameSpace="listing" translationKey="editRestaurantsProperties" size="s" />
			</Link>
		</div>
	);

	return (
		<div data-test={`Item-${index}`}>
			<div className={style.container}>
				<div className={subcategoryRowClasses}>
					<div className={style.imgWrapper}>
						<ImgUploaderByPreSignedUrl loaderSize="small" preSignedUrl={data.preSignedUrl} />
					</div>
					<Text
						className={subcategoryNameClasses}
						translationKey={data.name}
						withTooltip={true}
						data-test="subcategoryName"
					/>
				</div>
				<Text
					className={categoryNameRowClasses}
					translationKey={data.category.name}
					withTooltip={true}
					data-test="categoryName"
				/>
				<div className={isHiddenRowClasses}>
					<Badge color={data.isHidden ? 'red' : 'purple'}>
						<Text
							translationNameSpace="listing"
							translationKey={data.isHidden ? 'isHiddenFemaleLabel' : 'visibleFemaleLabel'}
							asFragment={true}
							data-test="isHidden"
						/>
					</Badge>
				</div>
				<div className={availableForRepetitionRowClasses}>
					<Badge color={data.availableForRepetition ? 'purple' : 'red'}>
						<Text
							translationNameSpace="listing"
							translationKey={data.availableForRepetition ? 'availableLabel' : 'unavailableLabel'}
							asFragment={true}
							data-test="availableForRepetition"
						/>
					</Badge>
				</div>
				<div className={actionRowClasses}>
					<DataController
						URLWithIDToInfo={`/menu-v2/subcategory/view?subcategoryID=${data.id}`}
						customEditButton={hasEditAccess && editButtonElement}
						onClickDelete={() => setIDForDeletion(data.id)}
						size="smallBtn"
					/>
				</div>
			</div>
		</div>
	);
};
